// Hash-based router for the admin portal.
//
// Pages register themselves at module-load time via `registerPage(...)` and
// the chrome owns the mount/unmount lifecycle. The router exposes `navigate`
// to chrome + pages (via PageContext) and listens for `hashchange` so the
// browser back/forward buttons work.
//
// Mirrors `apps/retailer/src/shell/router.ts` — same idempotency rules.

import type { Page, PageCleanup, PageContext } from "./types";

// ── Registry ────────────────────────────────────────────────────────────────

const pages = new Map<string, Page>();
const order: string[] = []; // preserves insertion order for the sidebar

/** Register a page. Idempotent — calling twice with the same name overwrites. */
export function registerPage(page: Page): void {
  if (!pages.has(page.name)) order.push(page.name);
  pages.set(page.name, page);
}

/** All registered pages in current order (for the sidebar to render). */
export function listPages(): readonly Page[] {
  return order.map((n) => pages.get(n)!).filter(Boolean);
}

export function getPage(name: string): Page | undefined {
  return pages.get(name);
}

/**
 * Replace the sidebar order with `canonical`, filtered to currently registered
 * pages. Pages not in `canonical` are appended at the end (preserving their
 * existing relative order). Call this once at the bottom of the page-registry
 * module to enforce a deterministic sidebar layout regardless of how ES
 * module imports were hoisted.
 */
export function setOrder(canonical: string[]): void {
  const seen = new Set<string>();
  const next: string[] = [];
  for (const name of canonical) {
    if (pages.has(name) && !seen.has(name)) {
      next.push(name);
      seen.add(name);
    }
  }
  for (const name of order) {
    if (!seen.has(name)) {
      next.push(name);
      seen.add(name);
    }
  }
  order.length = 0;
  order.push(...next);
}

// ── Mount lifecycle ─────────────────────────────────────────────────────────

interface RouterRuntime {
  container: HTMLElement;
  ctx: PageContext;
  defaultPage: string;
  currentName: string | null;
  currentCleanup: PageCleanup | null;
}

let runtime: RouterRuntime | null = null;
const navListeners = new Set<(name: string) => void>();

/** Subscribe to navigation events (sidebar uses this to update active state). */
export function onNavigate(fn: (name: string) => void): () => void {
  navListeners.add(fn);
  return () => {
    navListeners.delete(fn);
  };
}

export function getCurrent(): string | null {
  return runtime?.currentName ?? null;
}

/** Parse the page name out of `location.hash` (`#/foo` → `foo`). */
function parseHash(): string | null {
  const h = window.location.hash || "";
  const m = h.match(/^#\/([a-z0-9-]+)/i);
  return m?.[1]?.toLowerCase() ?? null;
}

function writeHash(name: string): void {
  const target = `#/${name}`;
  if (window.location.hash !== target) {
    window.location.hash = target;
  }
}

function resolvePageName(requested: string | null): string {
  if (requested && pages.has(requested)) return requested;
  if (runtime && pages.has(runtime.defaultPage)) return runtime.defaultPage;
  return order[0] ?? "dashboard";
}

function mountPage(name: string): void {
  if (!runtime) return;

  // Tear down the previous page first.
  if (runtime.currentCleanup) {
    try {
      runtime.currentCleanup();
    } catch (err) {
      console.warn(`[router] cleanup of "${runtime.currentName}" threw:`, err);
    }
    runtime.currentCleanup = null;
  }

  runtime.container.replaceChildren();

  const page = pages.get(name);
  if (!page) {
    runtime.container.innerHTML =
      `<div class="adm-page-missing">No page registered for "${name}".</div>`;
    runtime.currentName = name;
    return;
  }

  let cleanup: PageCleanup;
  try {
    cleanup = page.mount(runtime.container, runtime.ctx);
  } catch (err) {
    console.error(`[router] mount of "${name}" threw:`, err);
    runtime.container.innerHTML =
      `<div class="adm-page-error">Page "${name}" failed to load. See console.</div>`;
    runtime.currentName = name;
    return;
  }

  runtime.currentCleanup = typeof cleanup === "function" ? cleanup : () => {};
  runtime.currentName = name;
  for (const fn of navListeners) fn(name);
}

/** Programmatic navigation. */
export function navigate(name: string): void {
  if (!runtime) return;
  const resolved = resolvePageName(name);
  // Setting the hash will fire `hashchange`, which mounts the page.
  // If the hash is already correct, mount directly so re-clicking the active
  // sidebar entry re-renders the page.
  if (parseHash() === resolved) {
    mountPage(resolved);
  } else {
    writeHash(resolved);
  }
}

/**
 * Start the router. Mounts the page for the current hash (or `defaultPage`
 * if the hash is empty/unknown) and begins listening for hash changes.
 *
 * Returns a teardown function that detaches listeners and unmounts.
 */
export function startRouter(opts: {
  container: HTMLElement;
  ctx: PageContext;
  defaultPage: string;
}): () => void {
  runtime = {
    container: opts.container,
    ctx: opts.ctx,
    defaultPage: opts.defaultPage,
    currentName: null,
    currentCleanup: null,
  };

  const onHashChange = (): void => {
    const requested = parseHash();
    const resolved = resolvePageName(requested);
    if (requested !== resolved) {
      // Unknown / missing hash → quietly correct it without adding history.
      const target = `#/${resolved}`;
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}${target}`,
      );
    }
    if (resolved !== runtime?.currentName) {
      mountPage(resolved);
    }
  };

  window.addEventListener("hashchange", onHashChange);

  // Initial mount.
  onHashChange();

  return () => {
    window.removeEventListener("hashchange", onHashChange);
    if (runtime?.currentCleanup) {
      try {
        runtime.currentCleanup();
      } catch {
        /* ignore */
      }
    }
    runtime = null;
  };
}
